import {FC, useState} from 'react';
import {Box, Pagination, Paper, Typography} from '@mui/material';
import {useIntl} from 'react-intl';
import {ConnectorMeterValueDTO} from 'src/types/transactions';
import {convertMinutes} from 'src/utils/helper';
import {formatLocationTime} from 'src/utils/locationTime';

interface MeterValuesProps {
    meterValues: ConnectorMeterValueDTO[];
    wattsConsumed: number;
    connectedTimeMinutes: number;
    /** The charger's own clock, so a reading reads the same here as it does on the station. */
    locationTimezone?: string;
}

const ITEMS_PER_PAGE = 10;

export const MeterValues: FC<MeterValuesProps> = ({
                                                      meterValues,
                                                      wattsConsumed,
                                                      connectedTimeMinutes,
                                                      locationTimezone
                                                  }) => {
    const intl = useIntl();
    const [page, setPage] = useState(1);

    const totalItems = meterValues?.length || 0;
    const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

    const paginatedItems = meterValues?.slice(
        (page - 1) * ITEMS_PER_PAGE,
        page * ITEMS_PER_PAGE
    ) || [];

    const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    return (
        <Box>
            {/* Summary Cards */}
            <Paper
                sx={{
                    padding: 2,
                    backgroundColor: '#32324D',
                    borderRadius: 2,
                    marginBottom: 3,
                }}
            >
                <Box sx={{display: 'flex', justifyContent: 'space-around', alignItems: 'center', gap: 3}}>
                    {/* Session Consumption */}
                    <Box sx={{textAlign: 'center', flex: 1}}>
                        <Typography variant="body2"
                                    sx={{color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem', marginBottom: 0.5}}>
                            {intl.formatMessage({id: 'sessionConsumption'})}
                        </Typography>
                        <Typography variant="h5" fontWeight={700} sx={{color: 'white'}}>
                            {(wattsConsumed / 1000).toFixed(3)} {intl.formatMessage({id: 'kWh'})}
                        </Typography>
                    </Box>

                    {/* Connected Time */}
                    <Box sx={{textAlign: 'center', flex: 1}}>
                        <Typography variant="body2"
                                    sx={{color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem', marginBottom: 0.5}}>
                            {intl.formatMessage({id: 'connectedTime'})}
                        </Typography>
                        <Typography variant="h5" fontWeight={700} sx={{color: 'white'}}>
                            {convertMinutes(connectedTimeMinutes)}
                        </Typography>
                    </Box>

                    {/* Total Meter Values */}
                    <Box sx={{textAlign: 'center', flex: 1}}>
                        <Typography variant="body2"
                                    sx={{color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem', marginBottom: 0.5}}>
                            {intl.formatMessage({id: 'totalMeterValues'})}
                        </Typography>
                        <Typography variant="h5" fontWeight={700} sx={{color: 'white'}}>
                            {totalItems}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Meter Values Details */}
            <Box>
                {paginatedItems.map((item, index) => {
                    const actualIndex = (page - 1) * ITEMS_PER_PAGE + index;
                    return (
                        <Paper
                            key={item.id}
                            sx={{
                                padding: 0,
                                marginBottom: 3,
                                backgroundColor: 'background.paper',
                                borderLeft: '4px solid',
                                borderLeftColor: '#32324D',
                                overflow: 'hidden',
                                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                            }}
                        >
                            <Box sx={{padding: 1.5}}>
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 0.75
                                }}>
                                    <Typography variant="body2" fontWeight={600} sx={{fontSize: '0.875rem'}}>
                                        {intl.formatMessage({id: 'meterValue'})} #{actualIndex + 1}
                                    </Typography>
                                </Box>

                                <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 1.5}}>
                                    <Box>
                                        <Typography variant="caption" sx={{
                                            color: 'rgba(0, 0, 0, 0.6)',
                                            fontWeight: 500,
                                            fontSize: '0.7rem'
                                        }}>
                                            {intl.formatMessage({id: 'time'})}
                                            {locationTimezone ? ` (${locationTimezone})` : ''}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600}
                                                    sx={{color: '#32324D', fontSize: '0.8rem'}}>
                                            {formatLocationTime(item.valueTimestamp, locationTimezone)}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{
                                            color: 'rgba(0, 0, 0, 0.6)',
                                            fontWeight: 500,
                                            fontSize: '0.7rem'
                                        }}>
                                            {intl.formatMessage({id: 'measurand'})}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600}
                                                    sx={{color: '#32324D', fontSize: '0.8rem'}}>
                                            {item.measurand || '-'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{
                                            color: 'rgba(0, 0, 0, 0.6)',
                                            fontWeight: 500,
                                            fontSize: '0.7rem'
                                        }}>
                                            {intl.formatMessage({id: 'value'})}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600}
                                                    sx={{color: '#32324D', fontSize: '0.8rem'}}>
                                            {item.value || '-'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{
                                            color: 'rgba(0, 0, 0, 0.6)',
                                            fontWeight: 500,
                                            fontSize: '0.7rem'
                                        }}>
                                            {intl.formatMessage({id: 'unit'})}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600}
                                                    sx={{color: '#32324D', fontSize: '0.8rem'}}>
                                            {item.unit || '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Paper>
                    );
                })}

                {/* Pagination */}
                {totalPages > 1 && (
                    <Box sx={{display: 'flex', justifyContent: 'center', marginTop: 4}}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={handlePageChange}
                            color="primary"
                            size="large"
                            showFirstButton
                            showLastButton
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};